import { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { Form } from '@musinsa/pbo-core'
import PageHeader from '@/components/pbo/PageHeader'
import { notice } from '@/components/pbo/notice'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { PboScope } from '@/components/pbo/PboScope'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useGetShipperOptions } from '@/services/inventory/stockLedger/useStockLedgerQueries'
import {
  DAILY_CLOSE_PRODUCT_HEADS_EXCEL_URL,
  DAILY_CLOSE_PRODUCT_ITEMS_EXCEL_URL,
} from '@/services/inventory/dailyCloseProduct/apis'
import { useGetDailyCloseProductHeads, useGetDailyCloseProductItems } from '@/services/inventory/dailyCloseProduct/useDailyCloseProductQueries'
import type {
  DailyCloseProductHead,
  DailyCloseProductItemSearch,
  DailyCloseProductSearch,
} from '@/services/inventory/dailyCloseProduct/types'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { DETAIL_DEFAULT_PAGE_SIZE, DETAIL_MAX_PAGE_SIZE, SCREEN_TITLE } from './constants'
import { DailyCloseProductFilter } from './components/DailyCloseProductFilter'
import { DailyCloseProductTables } from './components/DailyCloseProductTables'
import type { DailyCloseProductFormValues } from './filterToRequest'
import { toHeadSearch, validateDailyCloseProductForm } from './filterToRequest'
import { DailyCloseProductSearchQuery, fromSearchQuery, hasDailyCloseProductSearchQuery, toSearchQuery } from './searchQuery'

type ExcelTarget = 'head' | 'item'
interface HeadView {
  search: DailyCloseProductSearch
  seq: number
}

function DailyCloseProduct() {
  const [form] = Form.useForm()
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<DailyCloseProductSearchQuery>()
  const scope = useMemo(() => withCoMingle(shipperScopeOf(AuthStorage.user.get()), true), [])
  const defaults = useMemo<DailyCloseProductFormValues>(() => ({
    shipperCode: defaultShipper(scope).shipperCode || undefined,
    closeDateRange: [dayjs(), dayjs()],
  }), [scope])
  const [initialValues] = useState<DailyCloseProductFormValues>(() => {
    const restored = fromSearchQuery(parsedQs, defaults)
    if (scope.locked) restored.shipperCode = scopedShipperCode(scope, restored.shipperCode)
    return restored
  })
  const hasRestoredSearch = hasDailyCloseProductSearchQuery(parsedQs)
  const [headView, setHeadView] = useState<HeadView | null>(() => {
    if (!hasRestoredSearch || validateDailyCloseProductForm(initialValues)) return null
    return { search: toHeadSearch(initialValues), seq: 1 }
  })
  const seqRef = useRef(headView?.seq ?? 0)
  const [itemRequest, setItemRequest] = useState<DailyCloseProductItemSearch | null>(null)

  const headsQuery = useGetDailyCloseProductHeads(headView?.search ?? null, headView?.seq ?? 0)
  const itemsQuery = useGetDailyCloseProductItems(itemRequest)
  const { options: allShipperOptions } = useGetShipperOptions()
  const shipperOptions = useMemo(
    () => scope.locked
      ? selectableShippers(scope).map((shipper) => ({ value: shipper.shipperCode, label: shipper.shipperName || shipper.shipperCode }))
      : allShipperOptions,
    [allShipperOptions, scope],
  )
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '일마감조회_상품별', config: { method: 'post' }, errorToast: true })

  const handleSearch = useCallback((rawValues: DailyCloseProductFormValues) => {
    const values = scope.locked ? { ...rawValues, shipperCode: scopedShipperCode(scope, rawValues.shipperCode) } : rawValues
    const message = validateDailyCloseProductForm(values)
    if (message) { notice.invalid(message); return }

    const search = toHeadSearch(values)
    seqRef.current += 1
    setHeadView({ search, seq: seqRef.current })
    setItemRequest(null)
    const query = toSearchQuery(values)
    MenuStorage.menuOnSession.setSearch(query)
    replaceQuery(query)
    menuSession.reload()
  }, [menuSession, replaceQuery, scope])

  const handleReset = useCallback(() => {
    const today = dayjs()
    form.setFieldsValue({
      ...defaults,
      closeDateRange: [today, today],
      brandName: undefined,
      goodsCode: undefined,
      barcodes: undefined,
      logisticsServiceType: undefined,
    })
    MenuStorage.menuOnSession.setSearch({})
    replaceQuery({})
    menuSession.reload()
  }, [defaults, form, menuSession, replaceQuery])

  const handleHeadDoubleClick = useCallback((row: DailyCloseProductHead) => {
    if (!headView || !row.closeDate || !row.warehouseName) return
    setItemRequest({
      ...headView.search,
      shipperCode: row.shipperCode,
      closeDateFrom: row.closeDate,
      closeDateTo: row.closeDate,
      warehouseName: row.warehouseName,
      page: 1,
      pageSize: DETAIL_DEFAULT_PAGE_SIZE,
    })
  }, [headView])

  const handleItemPage = useCallback((page: number, pageSize: number) => {
    setItemRequest((current) => {
      if (!current) return current
      const nextPageSize = Math.min(pageSize, DETAIL_MAX_PAGE_SIZE)
      return { ...current, page: nextPageSize === current.pageSize ? page : 1, pageSize: nextPageSize }
    })
  }, [])

  const handleDownload = useCallback((target: ExcelTarget) => {
    if (target === 'head' && headView) {
      onDownloadExcel(DAILY_CLOSE_PRODUCT_HEADS_EXCEL_URL, { ...headView.search }).catch(() => undefined)
    } else if (target === 'item' && itemRequest) {
      onDownloadExcel(DAILY_CLOSE_PRODUCT_ITEMS_EXCEL_URL, { ...itemRequest }).catch(() => undefined)
    }
  }, [headView, itemRequest, onDownloadExcel])

  const selectedHead = useMemo(
    () => itemRequest && { shipperCode: itemRequest.shipperCode, closeDate: itemRequest.closeDateFrom, warehouseName: itemRequest.warehouseName },
    [itemRequest],
  )

  const itemPageInfo = itemsQuery.data?.pageInfo ?? {
    page: itemRequest?.page ?? 1,
    size: itemRequest?.pageSize ?? DETAIL_DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  }

  return (
    <PboScope>
      <PageHeader title={SCREEN_TITLE} />
      <DailyCloseProductFilter
        form={form}
        initialValues={initialValues}
        shipperOptions={shipperOptions}
        canEditShipper={canSelectShipper(scope)}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <DailyCloseProductTables
        heads={headsQuery.data ?? []}
        items={itemsQuery.data?.items ?? []}
        itemPageInfo={itemPageInfo}
        selectedHead={selectedHead}
        isHeadLoading={headsQuery.isFetching}
        isItemLoading={itemsQuery.isFetching}
        isDownloading={isDownloading}
        onHeadDoubleClick={handleHeadDoubleClick}
        onItemPage={handleItemPage}
        onDownload={handleDownload}
      />
    </PboScope>
  )
}

export default DailyCloseProduct
